import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import axios from 'axios';

import {
  avisosApi,
  ListAvisosParams,
} from '../../services/api/avisos.api';
import {
  Aviso,
  AvisoDetail,
  AvisosFilters,
  CreateAvisoPayload,
  PaginatedAvisos,
  UpdateAvisoPayload,
} from '../../types/aviso';

export type AvisoMutationAction =
  | 'create'
  | 'update'
  | 'activate'
  | 'deactivate';

export interface AvisosState {
  items: Aviso[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  filters: AvisosFilters;
  status: 'idle' | 'loading' | 'error';
  error: string | null;
  selectedDetail: AvisoDetail | null;
  detailStatus: 'idle' | 'loading' | 'error';
  detailError: string | null;
  mutating: { id: string | null; action: AvisoMutationAction } | null;
  mutationError: string | null;
}

const initialState: AvisosState = {
  items: [],
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  filters: {},
  status: 'idle',
  error: null,
  selectedDetail: null,
  detailStatus: 'idle',
  detailError: null,
  mutating: null,
  mutationError: null,
};

export const messageFromError = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string | string[] }
      | undefined;
    if (data?.message) {
      return Array.isArray(data.message) ? data.message.join('; ') : data.message;
    }
    if (error.response?.status === 401) return 'Sua sessão expirou.';
    if (error.response?.status === 403)
      return 'Você não tem permissão para acessar este recurso.';
    if (error.code === 'ERR_NETWORK')
      return 'Não foi possível conectar ao servidor.';
  }
  return fallback;
};

export const fetchAvisosThunk = createAsyncThunk<
  PaginatedAvisos,
  ListAvisosParams,
  { rejectValue: string }
>('avisos/fetch', async (params, { rejectWithValue }) => {
  try {
    return await avisosApi.list(params);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar avisos'));
  }
});

export const fetchAvisoDetailThunk = createAsyncThunk<
  AvisoDetail,
  string,
  { rejectValue: string }
>('avisos/fetchDetail', async (id, { rejectWithValue }) => {
  try {
    return await avisosApi.detail(id);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao carregar aviso'));
  }
});

export const createAvisoThunk = createAsyncThunk<
  AvisoDetail,
  CreateAvisoPayload,
  { rejectValue: string }
>('avisos/create', async (payload, { rejectWithValue }) => {
  try {
    return await avisosApi.create(payload);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao criar aviso'));
  }
});

export const updateAvisoThunk = createAsyncThunk<
  AvisoDetail,
  { id: string; payload: UpdateAvisoPayload },
  { rejectValue: string }
>('avisos/update', async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await avisosApi.update(id, payload);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao atualizar aviso'));
  }
});

export const activateAvisoThunk = createAsyncThunk<
  AvisoDetail,
  string,
  { rejectValue: string }
>('avisos/activate', async (id, { rejectWithValue }) => {
  try {
    return await avisosApi.activate(id);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao ativar aviso'));
  }
});

export const deactivateAvisoThunk = createAsyncThunk<
  AvisoDetail,
  string,
  { rejectValue: string }
>('avisos/deactivate', async (id, { rejectWithValue }) => {
  try {
    return await avisosApi.deactivate(id);
  } catch (e) {
    return rejectWithValue(messageFromError(e, 'Erro ao desativar aviso'));
  }
});

const avisosSlice = createSlice({
  name: 'avisos',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<AvisosFilters>) {
      state.filters = action.payload;
      state.page = 1;
    },
    clearFilters(state) {
      state.filters = {};
      state.page = 1;
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = Math.max(1, action.payload);
    },
    setLimit(state, action: PayloadAction<number>) {
      state.limit = action.payload;
      state.page = 1;
    },
    clearSelectedDetail(state) {
      state.selectedDetail = null;
      state.detailStatus = 'idle';
      state.detailError = null;
    },
    clearMutationError(state) {
      state.mutationError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAvisosThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchAvisosThunk.fulfilled, (state, action) => {
        state.status = 'idle';
        state.items = action.payload.data;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.total = action.payload.total;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchAvisosThunk.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? 'Erro ao carregar avisos';
      })

      .addCase(fetchAvisoDetailThunk.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchAvisoDetailThunk.fulfilled, (state, action) => {
        state.detailStatus = 'idle';
        state.selectedDetail = action.payload;
      })
      .addCase(fetchAvisoDetailThunk.rejected, (state, action) => {
        state.detailStatus = 'error';
        state.detailError = action.payload ?? 'Erro ao carregar aviso';
      })

      .addCase(createAvisoThunk.pending, (state) => {
        state.mutating = { id: null, action: 'create' };
        state.mutationError = null;
      })
      .addCase(createAvisoThunk.fulfilled, (state) => {
        state.mutating = null;
      })
      .addCase(createAvisoThunk.rejected, (state, action) => {
        state.mutating = null;
        state.mutationError = action.payload ?? 'Erro ao criar aviso';
      })

      .addCase(updateAvisoThunk.pending, (state, action) => {
        state.mutating = { id: action.meta.arg.id, action: 'update' };
        state.mutationError = null;
      })
      .addCase(updateAvisoThunk.fulfilled, (state, action) => {
        state.mutating = null;
        if (state.selectedDetail?.id === action.payload.id) {
          state.selectedDetail = action.payload;
        }
      })
      .addCase(updateAvisoThunk.rejected, (state, action) => {
        state.mutating = null;
        state.mutationError = action.payload ?? 'Erro ao atualizar aviso';
      })

      .addCase(activateAvisoThunk.pending, (state, action) => {
        state.mutating = { id: action.meta.arg, action: 'activate' };
        state.mutationError = null;
      })
      .addCase(activateAvisoThunk.fulfilled, (state, action) => {
        state.mutating = null;
        if (state.selectedDetail?.id === action.payload.id) {
          state.selectedDetail = action.payload;
        }
      })
      .addCase(activateAvisoThunk.rejected, (state, action) => {
        state.mutating = null;
        state.mutationError = action.payload ?? 'Erro ao ativar aviso';
      })

      .addCase(deactivateAvisoThunk.pending, (state, action) => {
        state.mutating = { id: action.meta.arg, action: 'deactivate' };
        state.mutationError = null;
      })
      .addCase(deactivateAvisoThunk.fulfilled, (state, action) => {
        state.mutating = null;
        if (state.selectedDetail?.id === action.payload.id) {
          state.selectedDetail = action.payload;
        }
      })
      .addCase(deactivateAvisoThunk.rejected, (state, action) => {
        state.mutating = null;
        state.mutationError = action.payload ?? 'Erro ao desativar aviso';
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setPage,
  setLimit,
  clearSelectedDetail,
  clearMutationError,
} = avisosSlice.actions;

export default avisosSlice.reducer;
